/**
 * useDrugInteractionCheck
 *
 * Fetches real drug-drug interaction and allergy contraindication data for a
 * patient from the FHIR subgraph. Replaces the mock ClinicalSafetyEngine
 * interaction/allergy generation in AdvancedClinicalSafetyMonitor.
 *
 * Queries:
 *   fhirMedicinalProductInteractions(tenantId, subject) → DrugInteraction[]
 *   fhirAllergyIntolerances(tenantId, patientId)        → ClinicalAlert[] (ALLERGY_RISK)
 *
 * SDL types are defined in deploy/router/schemas/fhir/fhir.graphql.
 * These are NOT yet in generated/graphql.ts (pending SDL sync after gateway
 * restart). Inline gql + manual types used until next codegen run.
 *
 * DRR-114: FhirMedicinalProductInteraction = T2 (operational, 1yr).
 *          FhirAllergyIntolerance = T2 (operational, 1yr).
 */

import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useMemo } from 'react';
import type { DrugInteraction, ClinicalAlert } from './AdvancedClinicalSafetyMonitor';

// ─────────────────────────────────────────────────────────────────────────────
// GraphQL documents (inline — SDL types not yet in generated/graphql.ts)
// ─────────────────────────────────────────────────────────────────────────────

const DRUG_INTERACTIONS_QUERY = gql`
  query GetDrugInteractionsForPatient($tenantId: ID!, $subject: String) {
    fhirMedicinalProductInteractions(tenantId: $tenantId, limit: 50) {
      id
      description
      medicinalProductInteractionSubject {
        reference
        display
      }
      medicinalProductInteractionType {
        text
      }
      medicinalProductInteractionEffect {
        text
      }
      medicinalProductInteractionIncidence {
        text
      }
      medicinalProductInteractionManagement {
        text
      }
    }
  }
`;

const ALLERGY_INTOLERANCES_QUERY = gql`
  query GetAllergyIntolerancesForPatient($tenantId: ID!) {
    fhirAllergyIntolerances(tenantId: $tenantId, limit: 100) {
      id
      clinicalStatus {
        text
      }
      verificationStatus {
        text
      }
      type
      category
      criticality
      code {
        text
        coding {
          system
          code
          display
        }
      }
      patient {
        reference
        display
      }
      reaction {
        manifestation {
          text
        }
        severity
        description
      }
      note {
        text
      }
    }
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// Raw FHIR response shapes (minimal — only fields we use)
// ─────────────────────────────────────────────────────────────────────────────

interface FhirReference {
  reference?: string | null;
  display?: string | null;
}

interface FhirCodeableText {
  text?: string | null;
}

interface FhirCoding {
  system?: string | null;
  code?: string | null;
  display?: string | null;
}

interface FhirCodeWithCoding extends FhirCodeableText {
  coding?: FhirCoding[] | null;
}

interface FhirAllergyReaction {
  manifestation?: FhirCodeableText[] | null;
  severity?: string | null;
  description?: string | null;
}

interface RawFhirInteraction {
  id: string;
  description?: string | null;
  medicinalProductInteractionSubject?: FhirReference[] | null;
  medicinalProductInteractionType?: FhirCodeableText | null;
  medicinalProductInteractionEffect?: FhirCodeableText | null;
  medicinalProductInteractionIncidence?: FhirCodeableText | null;
  medicinalProductInteractionManagement?: FhirCodeableText | null;
}

interface RawFhirAllergyIntolerance {
  id: string;
  clinicalStatus?: FhirCodeableText | null;
  verificationStatus?: FhirCodeableText | null;
  criticality?: string | null;
  code?: FhirCodeWithCoding | null;
  patient?: FhirReference | null;
  reaction?: FhirAllergyReaction[] | null;
  note?: Array<{ text?: string | null }> | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Mappers: FHIR → component types
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Maps FHIR MedicinalProductInteraction.type.text to the DrugInteraction
 * interactionType discriminator.
 * Zen Engine rule rl-4-2-002 uses: CONTRAINDICATED | MAJOR | MODERATE | MINOR
 */
function mapInteractionType(
  typeText: string | null | undefined,
): DrugInteraction['interactionType'] {
  const t = (typeText ?? '').toUpperCase();
  if (t.includes('CONTRAINDICATED')) return 'CONTRAINDICATED';
  if (t.includes('MAJOR')) return 'MAJOR';
  if (t.includes('MINOR')) return 'MINOR';
  return 'MODERATE';
}

function interactionTypeSeverityScore(type: DrugInteraction['interactionType']): number {
  switch (type) {
    case 'CONTRAINDICATED': return 95;
    case 'MAJOR': return 80;
    case 'MODERATE': return 55;
    case 'MINOR': return 25;
  }
}

function mapFhirInteraction(raw: RawFhirInteraction): DrugInteraction | null {
  const subjects = raw.medicinalProductInteractionSubject ?? [];
  const drug1 = subjects[0]?.display ?? subjects[0]?.reference ?? 'Unknown Drug 1';
  const drug2 = subjects[1]?.display ?? subjects[1]?.reference ?? 'Unknown Drug 2';

  // Skip records with no usable drug names
  if (drug1 === 'Unknown Drug 1' && drug2 === 'Unknown Drug 2' && !raw.description) {
    return null;
  }

  const interactionType = mapInteractionType(raw.medicinalProductInteractionType?.text);

  return {
    drug1,
    drug2,
    interactionType,
    mechanism: raw.description ?? 'See interaction management notes',
    clinicalEffect: raw.medicinalProductInteractionEffect?.text ?? 'Clinical effect not specified',
    management: raw.medicinalProductInteractionManagement?.text ?? 'Consult pharmacist',
    severity: interactionTypeSeverityScore(interactionType),
    evidence: 'ESTABLISHED',  // All FHIR-stored interactions are considered established
    references: [],
  };
}

/**
 * Maps a FHIR AllergyIntolerance to a ALLERGY_RISK ClinicalAlert.
 * Only active + confirmed allergies generate alerts.
 */
function mapFhirAllergyToAlert(raw: RawFhirAllergyIntolerance): ClinicalAlert | null {
  const clinical = (raw.clinicalStatus?.text ?? '').toLowerCase();
  const verification = (raw.verificationStatus?.text ?? '').toLowerCase();

  // Only active + confirmed/unconfirmed (not refuted/entered-in-error)
  if (clinical === 'inactive' || clinical === 'resolved') return null;
  if (verification === 'refuted' || verification === 'entered-in-error') return null;

  const criticality = (raw.criticality ?? 'low').toUpperCase();
  const allergenDisplay =
    raw.code?.coding?.[0]?.display ??
    raw.code?.text ??
    'Unknown Allergen';

  const primaryReaction = raw.reaction?.[0];
  const reactionDesc =
    primaryReaction?.description ??
    primaryReaction?.manifestation?.[0]?.text ??
    'Reaction not documented';
  const reactionSeverity = (primaryReaction?.severity ?? 'mild').toUpperCase();

  const severity: ClinicalAlert['severity'] =
    criticality === 'HIGH' || reactionSeverity === 'SEVERE'
      ? 'CRITICAL'
      : criticality === 'LOW' && reactionSeverity !== 'SEVERE'
        ? 'MEDIUM'
        : 'HIGH';

  return {
    id: `allergy-${raw.id}`,
    type: 'ALLERGY_RISK',
    severity,
    title: `Documented Allergy: ${allergenDisplay}`,
    description: `Patient has a documented ${allergenDisplay} allergy. Reaction: ${reactionDesc}`,
    recommendation:
      raw.note?.[0]?.text ??
      'Review medication choice. Ensure allergy is considered before ordering medications in this drug class.',
    triggeredAt: Date.now(),
    status: 'ACTIVE',
    evidence: [
      {
        type: 'CLINICAL_OBSERVATION',
        value: `Reaction: ${reactionDesc} (${reactionSeverity})`,
        timestamp: Date.now(),
        source: 'FHIR AllergyIntolerance',
      },
    ],
    interventionRequired: criticality === 'HIGH',
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────

export interface DrugInteractionCheckResult {
  interactions: DrugInteraction[];
  allergyAlerts: ClinicalAlert[];
  loading: boolean;
  error: Error | undefined;
  refetch: () => void;
}

/**
 * Fetches live drug-drug interaction and allergy data for a patient.
 *
 * @param tenantId  - FERP tenant ID (required)
 * @param patientActorId - FERP sequential actor ID for the patient (optional;
 *   when absent, returns empty arrays — gracefully handles pages where no
 *   patient is selected)
 */
export function useDrugInteractionCheck(
  tenantId: string,
  patientActorId: string | null | undefined,
): DrugInteractionCheckResult {
  const skip = !tenantId || !patientActorId;

  const {
    data: interactionData,
    loading: interactionLoading,
    error: interactionError,
    refetch: refetchInteractions,
  } = useQuery<{ fhirMedicinalProductInteractions: RawFhirInteraction[] }>(DRUG_INTERACTIONS_QUERY, {
    variables: { tenantId, subject: patientActorId },
    skip,
    fetchPolicy: 'cache-and-network',
  });

  const {
    data: allergyData,
    loading: allergyLoading,
    error: allergyError,
    refetch: refetchAllergies,
  } = useQuery<{ fhirAllergyIntolerances: RawFhirAllergyIntolerance[] }>(ALLERGY_INTOLERANCES_QUERY, {
    variables: { tenantId },
    skip,
    fetchPolicy: 'cache-and-network',
  });

  const interactions = useMemo<DrugInteraction[]>(() => {
    const raw: RawFhirInteraction[] =
      interactionData?.fhirMedicinalProductInteractions ?? [];
    return raw.flatMap((r) => {
      const mapped = mapFhirInteraction(r);
      return mapped ? [mapped] : [];
    });
  }, [interactionData]);

  const allergyAlerts = useMemo<ClinicalAlert[]>(() => {
    const raw: RawFhirAllergyIntolerance[] =
      allergyData?.fhirAllergyIntolerances ?? [];
    return raw.flatMap((r) => {
      const mapped = mapFhirAllergyToAlert(r);
      return mapped ? [mapped] : [];
    });
  }, [allergyData]);

  function refetch() {
    refetchInteractions();
    refetchAllergies();
  }

  return {
    interactions,
    allergyAlerts,
    loading: interactionLoading || allergyLoading,
    error: interactionError ?? allergyError,
    refetch,
  };
}
